import { useState, useEffect } from 'react';
import { Buildings, Plus, X, Warning, Check, PencilSimple, Spinner, MapPin } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

const TYPE_COLORS = {
  factory: 'hsl(197,100%,48%)',
  warehouse: 'hsl(43,96%,56%)',
  office: 'hsl(142,70%,45%)',
  branch: 'hsl(280,60%,60%)',
};
const STATUS_COLOR = {
  active: 'hsl(142,70%,45%)',
  inactive: 'hsl(0,0%,40%)',
  maintenance: 'hsl(43,96%,56%)',
};
const CAPABILITIES = ['production', 'qc', 'dispatch', 'storage', 'office', 'procurement'];
const LOCATION_TYPES = ['factory', 'warehouse', 'office', 'branch'];

const EMPTY_FORM = {
  code: '', name: '', type: 'factory', status: 'active',
  gstin: '', address: { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' },
  capabilities: [], production_lines: [], storage_zones: [],
  contact: { name: '', phone: '', email: '' },
};

export default function Locations() {
  const { user } = useAuth();
  const { refreshLocations } = useLocationContext();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', data: {} }
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = () => {
    setLoading(true);
    api.get('/locations').then(r => setLocations(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const openAdd = () => { setForm(EMPTY_FORM); setModal({ mode: 'add' }); };
  const openEdit = (loc) => {
    setForm({
      ...EMPTY_FORM, ...loc,
      address: { ...EMPTY_FORM.address, ...(loc.address || {}) },
      contact: { ...EMPTY_FORM.contact, ...(loc.contact || {}) },
      production_lines: Array.isArray(loc.production_lines) ? loc.production_lines : [],
      storage_zones: Array.isArray(loc.storage_zones) ? loc.storage_zones : [],
      capabilities: Array.isArray(loc.capabilities) ? loc.capabilities : [],
    });
    setModal({ mode: 'edit', code: loc.code });
  };
  const closeModal = () => { setModal(null); setForm(EMPTY_FORM); };

  const set = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const toggleCap = (cap) => {
    setForm(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      production_lines: typeof form.production_lines === 'string'
        ? form.production_lines.split(',').map(s => s.trim()).filter(Boolean)
        : form.production_lines,
      storage_zones: typeof form.storage_zones === 'string'
        ? form.storage_zones.split(',').map(s => s.trim()).filter(Boolean)
        : form.storage_zones,
    };
    try {
      if (modal.mode === 'add') {
        await api.post('/locations', payload);
        showToast(`Location "${payload.name}" created`);
      } else {
        await api.put(`/locations/${modal.code}`, payload);
        showToast(`Location "${payload.name}" updated`);
      }
      load();
      await refreshLocations();
      closeModal();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (code) => {
    if (!window.confirm(`Deactivate location ${code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/locations/${code}`);
      showToast(`Location ${code} deactivated`);
      load();
      await refreshLocations();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to deactivate', 'error');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <Warning size={48} color="hsl(0,84%,60%)" />
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, color: 'hsl(0,0%,60%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Access Restricted</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,40%)' }}>Location management is accessible to System Admins only.</div>
      </div>
    );
  }

  const activeLocs = locations.filter(l => l.status === 'active').length;

  return (
    <div data-testid="locations-page">
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px',
          background: toast.type === 'error' ? 'hsl(0,80%,45%)' : 'hsl(142,70%,40%)',
          color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          letterSpacing: '0.05em', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.type === 'error' ? <X size={14} /> : <Check size={14} weight="bold" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>
            Locations
          </h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {locations.length} Total · {activeLocs} Active · Factories, Warehouses & Offices
          </div>
        </div>
        <button onClick={openAdd} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> ADD LOCATION
        </button>
      </div>

      {/* Stats */}
      <div className="metrics-row" style={{ marginBottom: 24 }}>
        {LOCATION_TYPES.map(type => {
          const count = locations.filter(l => l.type === type && l.status === 'active').length;
          const color = TYPE_COLORS[type];
          return (
            <div key={type} style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{type}</div>
                <Buildings size={14} color={color} />
              </div>
              <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700, color }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 90px 90px 1fr 80px', padding: '10px 16px', borderBottom: '1px solid hsl(0,0%,12%)', gap: 12 }}>
          {['Code', 'Name', 'Type', 'Status', 'Capabilities', 'Actions'].map(h => (
            <div key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Spinner size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
          </div>
        ) : locations.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <MapPin size={32} color="hsl(0,0%,25%)" style={{ marginBottom: 12 }} />
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,40%)' }}>No locations yet. Add your first location.</div>
          </div>
        ) : locations.map(loc => (
          <div key={loc.code}
            style={{ display: 'grid', gridTemplateColumns: '100px 1fr 90px 90px 1fr 80px', padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,8%)', gap: 12, alignItems: 'center', transition: 'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(0,0%,6%)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: TYPE_COLORS[loc.type] || 'hsl(0,0%,70%)' }}>
              {loc.code}
            </div>

            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,85%)', fontWeight: 500 }}>{loc.name}</div>
              {loc.address?.city && (
                <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                  {loc.address.city}{loc.address.state ? `, ${loc.address.state}` : ''}
                </div>
              )}
            </div>

            <div>
              <span style={{ fontSize: 9, padding: '3px 8px', background: `${TYPE_COLORS[loc.type] || 'hsl(0,0%,30%)'}20`, color: TYPE_COLORS[loc.type] || 'hsl(0,0%,50%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.08em', border: `1px solid ${TYPE_COLORS[loc.type] || 'hsl(0,0%,20%)'}40` }}>
                {loc.type}
              </span>
            </div>

            <div>
              <span style={{ fontSize: 9, padding: '3px 8px', background: `${STATUS_COLOR[loc.status] || 'hsl(0,0%,30%)'}15`, color: STATUS_COLOR[loc.status] || 'hsl(0,0%,50%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {loc.status}
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(loc.capabilities || []).map(cap => (
                <span key={cap} style={{ fontSize: 8, padding: '2px 5px', background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,55%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', border: '1px solid hsl(0,0%,16%)' }}>
                  {cap}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => openEdit(loc)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,45%)', padding: 4, transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'hsl(197,100%,48%)'}
                onMouseLeave={e => e.currentTarget.style.color = 'hsl(0,0%,45%)'}>
                <PencilSimple size={14} />
              </button>
              {loc.status === 'active' && (
                <button onClick={() => handleDeactivate(loc.code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,40%)', padding: 4, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'hsl(0,84%,60%)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'hsl(0,0%,40%)'}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', padding: '20px 0' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 600, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)', margin: 0 }}>
                {modal.mode === 'add' ? 'Add Location' : `Edit: ${modal.code}`}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>

            <form onSubmit={handleSave}>
              {/* Basic */}
              <div style={{ marginBottom: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)', textTransform: 'uppercase', letterSpacing: '0.15em', borderBottom: '1px solid hsl(0,0%,10%)', paddingBottom: 6 }}>Basic Info</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={LBL}>Code *</label>
                  <input required style={{ ...INP, textTransform: 'uppercase' }} value={form.code} readOnly={modal.mode === 'edit'}
                    onChange={e => set('code', e.target.value.toUpperCase())} placeholder="PUN-F1" />
                </div>
                <div>
                  <label style={LBL}>Location Name *</label>
                  <input required style={INP} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Pune Factory 1" />
                </div>
                <div>
                  <label style={LBL}>Type *</label>
                  <select required style={{ ...INP }} value={form.type} onChange={e => set('type', e.target.value)}>
                    {LOCATION_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Status</label>
                  <select style={{ ...INP }} value={form.status} onChange={e => set('status', e.target.value)}>
                    {['active', 'inactive', 'maintenance'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={LBL}>GSTIN</label>
                  <input style={INP} value={form.gstin} onChange={e => set('gstin', e.target.value.toUpperCase())} placeholder="27AAAAA1234A1Z5" maxLength={15} />
                </div>
              </div>

              {/* Address */}
              <div style={{ marginBottom: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)', textTransform: 'uppercase', letterSpacing: '0.15em', borderBottom: '1px solid hsl(0,0%,10%)', paddingBottom: 6 }}>Address</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={LBL}>Line 1</label>
                  <input style={INP} value={form.address.line1} onChange={e => set('address.line1', e.target.value)} placeholder="Street / Plot" />
                </div>
                <div>
                  <label style={LBL}>Line 2</label>
                  <input style={INP} value={form.address.line2} onChange={e => set('address.line2', e.target.value)} placeholder="Area / Locality" />
                </div>
                <div>
                  <label style={LBL}>City</label>
                  <input style={INP} value={form.address.city} onChange={e => set('address.city', e.target.value)} placeholder="Pune" />
                </div>
                <div>
                  <label style={LBL}>State</label>
                  <input style={INP} value={form.address.state} onChange={e => set('address.state', e.target.value)} placeholder="Maharashtra" />
                </div>
                <div>
                  <label style={LBL}>PIN</label>
                  <input style={INP} value={form.address.pincode} onChange={e => set('address.pincode', e.target.value)} placeholder="411001" maxLength={6} />
                </div>
                <div>
                  <label style={LBL}>Country</label>
                  <input style={INP} value={form.address.country} onChange={e => set('address.country', e.target.value)} />
                </div>
              </div>

              {/* Capabilities */}
              <div style={{ marginBottom: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)', textTransform: 'uppercase', letterSpacing: '0.15em', borderBottom: '1px solid hsl(0,0%,10%)', paddingBottom: 6 }}>Capabilities</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {CAPABILITIES.map(cap => {
                  const active = form.capabilities.includes(cap);
                  return (
                    <button key={cap} type="button" onClick={() => toggleCap(cap)}
                      style={{ padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid', transition: 'all 0.15s',
                        background: active ? 'hsl(197,40%,12%)' : 'transparent',
                        borderColor: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,20%)',
                        color: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,45%)',
                      }}>
                      {cap}
                    </button>
                  );
                })}
              </div>

              {/* Production lines / Storage zones */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={LBL}>Production Lines (comma-separated)</label>
                  <input style={INP} value={Array.isArray(form.production_lines) ? form.production_lines.join(', ') : form.production_lines}
                    onChange={e => set('production_lines', e.target.value)}
                    placeholder="Line A, Line B, Line C" />
                </div>
                <div>
                  <label style={LBL}>Storage Zones (comma-separated)</label>
                  <input style={INP} value={Array.isArray(form.storage_zones) ? form.storage_zones.join(', ') : form.storage_zones}
                    onChange={e => set('storage_zones', e.target.value)}
                    placeholder="Zone 1, Zone 2" />
                </div>
              </div>

              {/* Contact */}
              <div style={{ marginBottom: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)', textTransform: 'uppercase', letterSpacing: '0.15em', borderBottom: '1px solid hsl(0,0%,10%)', paddingBottom: 6 }}>Site Contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={LBL}>Contact Name</label>
                  <input style={INP} value={form.contact.name} onChange={e => set('contact.name', e.target.value)} placeholder="e.g. Suresh Patil" />
                </div>
                <div>
                  <label style={LBL}>Phone</label>
                  <input style={INP} value={form.contact.phone} onChange={e => set('contact.phone', e.target.value)} placeholder="+91 9876543210" />
                </div>
                <div>
                  <label style={LBL}>Email</label>
                  <input style={INP} type="email" value={form.contact.email} onChange={e => set('contact.email', e.target.value)} placeholder="site@company.com" />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeModal} className="btn-ghost">Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
                  {saving ? <Spinner size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={13} weight="bold" />}
                  {modal.mode === 'add' ? 'CREATE LOCATION' : 'SAVE CHANGES'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
